import { useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import Highcharts from './mainModule';
import { Options } from 'highcharts/highstock';
import HighchartsReact, { HighchartsReactProps } from 'highcharts-react-official';
import React, { Ref, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import './highcharts.scss';
import { absoluteSettings, areaChartSettings, percentSettings } from './settings';
import './styles.scss';
import i18next from '../../i18n';
import LangOptions from './langOptions.json';

type ChartType = 'area' | 'line' | 'spline' | 'column' | 'bar';
type CompareType = 'absolute' | 'percent';

const getSettings = (
  chartType: ChartType,
  roundingDecimals: number,
  compareMode?: CompareType
): Options => {
  const baseSettings =
    compareMode === 'absolute'
      ? absoluteSettings(roundingDecimals)
      : percentSettings(roundingDecimals);

  const settings = chartType === 'area' ? areaChartSettings(baseSettings) : baseSettings;

  return {
    ...settings,
    chart: {
      ...settings.chart,
      type: chartType,
    },
  };
};

Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
    rangeSelectorZoom: '',
  },
  plotOptions: {
    series: {
      dataGrouping: {
        enabled: false,
      },
    },
  },
});

interface ChartProps extends HighchartsReactProps {
  compareMode?: CompareType;
  setupOptions?: (options: Options) => Options;
  width?: string;
  height?: string;
  title?: string;
  colors?: string[];
  roundingDecimals?: number;
}

const Chart = React.forwardRef((props: ChartProps, ref: Ref<any>) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [inmutable, setImmutable] = React.useState(true);

  const lang = i18next.language;
  const langOptsFromI18Next =
    (LangOptions as Record<string, Highcharts.LangOptions>)[lang] || LangOptions.en;

  const highchartsLangObj = {
    ...langOptsFromI18Next,
    thousandsSep: ',',
    rangeSelectorZoom: '',
  };
  useEffect(() => {
    Highcharts.setOptions({
      lang: highchartsLangObj,
      plotOptions: {
        series: {
          dataGrouping: {
            enabled: false,
          },
        },
      },
    });
  }, [lang]);

  const {
    series,
    chartType,
    setupOptions,
    containerProps,
    constructorType,
    width,
    height = '100%',
    compareMode,
    colors = [],
    roundingDecimals = 2,
  } = props;

  const chartSettings = {
    ...getSettings(chartType, roundingDecimals, compareMode),
    series,
  };

  // const settings = chartSettings;
  const settings = setupOptions ? setupOptions(chartSettings) : chartSettings;
  let customProps = { style: {} };
  if (height) {
    customProps = {
      style: {
        height,
      },
    };
  }
  if (width) {
    customProps = {
      style: {
        ...customProps.style,
        width,
      },
    };
  }
  const opts = {
    ...settings,
    colors,
  };

  const boxShadow = theme.palette.mode === 'light' ? `#00000029` : `#ffffff29`;

  const styleContent = `
  .highcharts-background {
    fill: ${theme.palette.background.paper};
  }
  .highcharts-range-input rect {
    fill: transparent;
    stroke: ${theme.palette.primary.main};
  }

  .highcharts-range-input text {
    fill: ${theme.palette.text.primary} !important;
    font-weight: bold;
  }

  .highcharts-button rect {
    fill: transparent;
    stroke: ${theme.palette.primary.main};
    stroke-width: 1;
  }

  .highcharts-no-tooltip.highcharts-button.highcharts-button-normal text {
    fill: ${theme.palette.primary.main} !important;
  }

  .highcharts-button-hover rect {
    fill: ${theme.palette.primary.main};
  }

  .highcharts-button-pressed rect {
    fill: ${theme.palette.primary.main} !important;
  }

  .highcharts-button-pressed text {
    fill: ${theme.palette.primary.contrastText} !important;
  }
  .highcharts-label text {
    fill: ${theme.palette.text.primary} !important;
  }

  .highcharts-label.highcharts-tooltip-box .highcharts-label-box{
    fill: ${theme.palette.primary.contrastText}80 !important;
  }

  .highcharts-axis-labels  text {
    fill: ${theme.palette.text.secondary} !important;
  }

  .highcharts-plot-line-label  {
    fill: ${theme.palette.text.secondary} !important;
  }

  ul.highcharts-menu {
    box-shadow: 0 3px 6px ${boxShadow} !important;
    background-color: ${theme.palette.background.paper} !important;
    border: none !important;
    border-radius: 5px !important;
    color: ${theme.palette.text.primary} !important;
  }

  ul.highcharts-menu li.highcharts-menu-item{
    color: ${theme.palette.text.primary} !important;
  }

  ul.highcharts-menu li.highcharts-menu-item:hover{
    background-color: ${theme.palette.primary.main} !important;
    color: ${theme.palette.primary.contrastText} !important;
  }

  ul.highcharts-menu hr{
    box-shadow: none !important;
    border: 1px solid ${theme.palette.divider} !important;
  }
  `;

  const pastYear = dayjs().subtract(1, 'year').valueOf();
  const today = dayjs().valueOf();

  if ((isMobile || isSmall) && opts.chart?.type === 'line' && constructorType === 'stockChart') {
    // limit the chart to the last year on mobile devices
    // to avoid performance issues
    if (opts.xAxis && !Array.isArray(opts.xAxis)) {
      opts.xAxis.min = pastYear;
      opts.xAxis.max = today;
    }
  }

  useEffect(() => {
    // redraw the chart when the mode changes from real time to historical
    if (opts.series) {
      const visble = opts.series[0]?.visible;
      if (visble !== inmutable) {
        setImmutable(opts.series[0]?.visible);
      }
    }
  }, [opts]);

  return (
    <>
      {theme.palette.mode === 'dark' && (
        <div dangerouslySetInnerHTML={{ __html: `<style>${styleContent}</style>` }} />
      )}

      <HighchartsReact
        ref={ref}
        highcharts={Highcharts}
        constructorType={constructorType}
        containerProps={{ ...containerProps, ...customProps }}
        options={opts}
        immutable={inmutable}
      />
    </>
  );
});

Chart.displayName = 'Chart';

export default Chart;
